import React, { useState, useMemo } from 'react';
import { Mail, Phone, MapPin, Clock, CheckCircle, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { PRICING, ALL_SERVICES, getServiceById, formatPriceRange, calculateEstimate } from '../data/pricing';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    preferredDate: '',
    message: ''
  });
  const [hours, setHours] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedService = useMemo(() => getServiceById(formData.serviceType), [formData.serviceType]);
  const estimate = useMemo(() => calculateEstimate(selectedService, parseFloat(hours)), [selectedService, hours]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, serviceType: value }));
    setHours('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = { ...formData };
      if (selectedService?.type === 'hourly' && hours) {
        submitData.message = `${submitData.message ? submitData.message + '\n' : ''}Estimated hours: ${hours}h | Est. range: £${estimate?.min}-£${estimate?.max}`.trim();
      }

      const response = await axios.post(`${API}/quotes`, submitData);
      
      if (response.status === 200) {
        toast.success('Quote request submitted successfully!', {
          description: "We'll respond within 24-48 hours with your personalized quote."
        });
        setFormData({ name: '', email: '', phone: '', serviceType: '', preferredDate: '', message: '' });
        setHours('');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote request', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Get a Free Quote"
        description="Request a free, no-obligation cleaning quote. Residential, commercial, and specialty cleaning services with transparent pricing."
        keywords="free cleaning quote, cleaning estimate, cleaning service quote London"
        canonical="/contact"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sky-50 to-emerald-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">Get Your Free Quote</h1>
            <p className="text-base sm:text-lg text-slate-600">
              Fill out the form below and we'll respond within 24-48 hours with a customized quote tailored to your needs
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-sky-100">
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="John Doe"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="john@example.com"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="07951 488 263"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="serviceType">Service Type *</Label>
                        <Select onValueChange={handleServiceChange} value={formData.serviceType} required>
                          <SelectTrigger className="mt-2" data-testid="select-service">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(PRICING).map(([key, category]) => (
                              <SelectGroup key={key}>
                                <SelectLabel className="text-sky-700 font-semibold">{category.label}</SelectLabel>
                                {category.services.map(service => (
                                  <SelectItem key={service.id} value={service.id}>
                                    {service.name}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Price Range Display */}
                    {selectedService && (
                      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4" data-testid="price-estimate">
                        <div className="flex items-start gap-3">
                          <Info className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-800">{selectedService.name}</p>
                            <p className="text-sm text-slate-600 mt-1">{selectedService.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className="bg-sky-600">{formatPriceRange(selectedService)}</Badge>
                              <span className="text-xs text-slate-500">{selectedService.unit}</span>
                            </div>

                            {/* Hours Estimator for hourly services */}
                            {selectedService.type === 'hourly' && (
                              <div className="mt-3 pt-3 border-t border-sky-200">
                                <Label htmlFor="hours" className="text-sm">Estimated hours needed</Label>
                                <div className="flex items-center gap-3 mt-1">
                                  <Input
                                    id="hours"
                                    type="number"
                                    min="1"
                                    max="20"
                                    step="0.5"
                                    value={hours}
                                    onChange={(e) => setHours(e.target.value)}
                                    placeholder="e.g. 3"
                                    className="w-24"
                                    data-testid="input-hours"
                                  />
                                  <span className="text-sm text-slate-500">hours</span>
                                  {estimate && (
                                    <Badge variant="outline" className="border-emerald-600 text-emerald-700" data-testid="estimated-total">
                                      Est. total: £{estimate.min} – £{estimate.max}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="preferredDate">Preferred Date</Label>
                      <Input
                        id="preferredDate"
                        name="preferredDate"
                        type="date"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        className="mt-2"
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="input-date"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Additional Details</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="mt-2"
                        rows={4}
                        placeholder="Tell us about your space size, specific cleaning needs, or any questions..."
                        data-testid="input-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                      size="lg"
                      disabled={isSubmitting}
                      data-testid="submit-quote-btn"
                    >
                      {isSubmitting ? 'Submitting...' : 'Request Quote'}
                    </Button>

                    <p className="text-sm text-slate-500 text-center">
                      We'll review your request and respond within 24-48 hours with a detailed quote.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              <Card className="border-sky-100">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Contact Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-gradient-to-br from-sky-100 to-emerald-100 p-2 rounded-lg">
                        <Phone className="h-5 w-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Phone</p>
                        <p className="text-sm text-slate-600">07951488263</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-gradient-to-br from-sky-100 to-emerald-100 p-2 rounded-lg">
                        <Mail className="h-5 w-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Email</p>
                        <p className="text-sm text-slate-600">Team@serene-world.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-gradient-to-br from-sky-100 to-emerald-100 p-2 rounded-lg">
                        <MapPin className="h-5 w-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Service Area</p>
                        <p className="text-sm text-slate-600">London, United Kingdom</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-gradient-to-br from-sky-100 to-emerald-100 p-2 rounded-lg">
                        <Clock className="h-5 w-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Business Hours</p>
                        <p className="text-sm text-slate-600">Mon-Fri: 8am - 7pm<br />Sat: 10am - 1pm</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-emerald-100 bg-gradient-to-br from-emerald-50 to-sky-50">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">What Happens Next?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700">We review your request and assess your needs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700">Receive a detailed, customized quote within 24-48 hours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700">Approve and book your preferred date & time</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700">Pay £15 deposit to confirm your booking</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
